import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HotkeyService } from 'src/app/core/services/hotkey.service';

import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import * as moment from 'moment';
import { ActionService } from '../action.service';
import { JobService } from '@intranet/modules/jobs/job.service';
import { ShortJob } from '@intranet/modules/jobs/job.types';
import { ClientsService } from '@intranet/modules/clients/clients.service';
import { Client } from '@intranet/modules/clients/client.types';
import { orderClientsByGroupName } from '@intranet/modules/clients/client.helpers';
import { LoginService } from '@intranet/modules/login/login.service';
import { UserService } from '@intranet/modules/users/user.service';
import {
    User,
    UserGroup,
    UserOrGroup,
} from '@intranet/modules/users/user.types';
import {
    ActionNote,
    JobAction,
    JobActionType,
} from '@intranet/modules/actions';
import {
    ActionRecurrence,
    ActionStatus,
    RecurrenceType,
} from '../action.types';
import {
    daysOfWeek,
    recurranceIntervals,
    statuses,
    priorities,
} from '../action.utils';
import { NgForm, ValidatorFn } from '@angular/forms';
import { RadioPickerItem } from '@intranet/shared/radiopicker/radiopicker.component';
import Quill from 'quill';

@Component({
    selector: 'action-manage',
    templateUrl: './action-manage.component.html',
    styleUrls: ['./action-manage.component.scss'],
})
export class ActionManageComponent implements OnInit, AfterViewInit {
    @ViewChild('actionForm', { static: false }) form: NgForm;
    @ViewChild('waitingUserControl', { static: false })
    waitingUserControlRef: ElementRef;
    @ViewChild('titleField') titleField: ElementRef;

    @Input() jobActionId: number;
    @Input() jobId: number;
    @Input() nextActionStatus: ActionStatus | null;
    @Input() propertyKey: string;
    @Input() readonly = false;

    @Output() changed = new EventEmitter<{
        action: JobAction;
        type: 'created' | 'create-multi' | 'updated' | 'deleted';
    }>();
    @Output() cancel = new EventEmitter();

    submitting = false;
    loggedInUserId: string;
    error: string;
    action: JobAction;
    /** True when this action's job is flagged as a Rock. Rock jobs auto-pin all
     *  their actions to the top regardless of type, so the action-type choice is
     *  redundant — we lock the dropdown and note why. */
    isRockJob = false;
    recurrence: ActionRecurrence | undefined | null;
    isNewRecurringAction = false;
    users: User[];
    usersInGroups: UserOrGroup[];
    userGroups: UserGroup[];
    assignees: UserOrGroup[];
    assignToGroup = false;
    monthNumbers: number[] = [];
    maxDayNumbers: number[] = [];
    dayNumbers: number[] = [];
    userOrGroupChanged: boolean = false;
    waitingOnUserOrGroupId: string | number = null;
    _recurrenceType = 'none';
    _userOrGroup = null;

    showEditJiraLinks = false;

    // Move to another job
    allClients: Client[] = [];
    clients: [Client[], Client[]] = [[], []];
    moveJobs: ShortJob[] = [];
    moveJobsLoading = false;

    protected modules = {
        toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
        ],
    };

    protected userIds: string[] = [];
    protected groupIds: number[] = [];
    protected waitingUserIds: string[] = [];
    protected waitingGroupIds: number[] = [];
    protected userSelectValid = true;
    protected addAnother = false;
    public ActionStatus = ActionStatus;

    private validateScheduleDate: ValidatorFn;
    private validateDeadlineDate: ValidatorFn;

    public assignmentModel: { userIds: string[]; groupIds: number[] } = {
        userIds: [],
        groupIds: []
    };

    constructor(
        private loginService: LoginService,
        private actionService: ActionService,
        private jobService: JobService,
        private clientsService: ClientsService,
        private userService: UserService,
        private route: ActivatedRoute,
        private hotkeyService: HotkeyService,
        private _changeDetector: ChangeDetectorRef
    ) {
        this.loggedInUserId = this.loginService.userId;

        for (var i = 1; i < 32; i++) {
            this.maxDayNumbers.push(i);
        }

        const dateValidator = (key: string) => {
            const clearTime = (date: Date) => {
                date.setHours(0, 0, 0, 0);
                return date;
            };

            return () => {
                let scheduleDate = this.form.form.get('scheduleDate')?.value;
                let deadlineDate = this.form.form.get('deadlineDate')?.value;

                if (scheduleDate && deadlineDate) {
                    scheduleDate = clearTime(new Date(scheduleDate));
                    deadlineDate = clearTime(new Date(deadlineDate));

                    if (deadlineDate < scheduleDate) {
                        return { [key]: 'error' };
                    }
                }

                return null;
            };
        };

        this.validateScheduleDate = dateValidator('scheduleDate');
        this.validateDeadlineDate = dateValidator('deadlineDate');

        this.dayNumbers = this.maxDayNumbers;
    }

    ngAfterViewInit(): void {
        if (this.titleField?.nativeElement) {
            this.titleField.nativeElement.focus();
        }
    }

    ngOnInit(): void {
        // Load clients for the "Move to another job" client selector
        combineLatest([
            this.clientsService.clients$,
            this.clientsService.myClients$,
        ])
            .pipe(take(1))
            .subscribe(([allClients, myClients]) => {
                allClients = allClients ?? [];
                const [pinnedClients, restClients] = orderClientsByGroupName(
                    allClients.filter(c => !c.archive),
                    (myClients ?? []).map(c => c.clientId),
                    true
                );
                this.allClients = allClients;
                this.clients = [pinnedClients as Client[], restClients as Client[]];
            });

        // Register hotkey for "Cancel" action (Ctrl + Q)
        this.hotkeyService.registerHotkey('ctrl+q', 'Cancel action', () => this.onCancel());

        // Register hotkey for "Save" action (Ctrl + Alt + S)
        this.hotkeyService.registerHotkey('ctrl+alt+s', 'Save action', () => {
            if (
                !this.submitting &&
                this.form?.valid &&
                !this.form?.pristine &&
                this.showEditJiraLinks === false
            ) {
                this.onSubmit(); // Trigger the Save button
            }
        });

        // Register hotkey for "Save and Add Another" action (Ctrl + O)
        this.hotkeyService.registerHotkey('ctrl+o', 'Save and Add Another action', () => {
            if (
                !this.submitting &&
                this.form?.valid &&
                !this.form?.pristine &&
                !this.action.jobActionId
            ) {
                this.addAnother = true; // Set the flag for saving and adding another
                this.onSubmit(); // Trigger the Save and Add Another button
            }
        });

        this.route.paramMap
            .pipe(
                switchMap((p) => {
                    // Get users and groups
                    this.users = this.userService.users.filter(
                        (user) => user.active
                    );
                    this.userGroups = this.userService.groups;

                    // Look in params for jobId
                    let jobActionId = p.get('jobActionId');
                    if (jobActionId) {
                        this.jobActionId = parseInt(jobActionId);
                    } else {
                        // Check if jobActionId is passed in via props
                        jobActionId =
                            this.route.snapshot.data?.jobActionId ||
                            this.jobActionId;
                    }

                    if (this.jobActionId) {
                        return this.actionService.get(this.jobActionId);
                    } else {
                        let jobId =
                            this.route.snapshot.data?.jobId || this.jobId;

                        return of({
                            success: true,
                            error: null,
                            jobAction: <JobAction>{
                                jobId,
                                isDeleted: false,
                                jobActionType: JobActionType.Standard,
                                actionStatus: ActionStatus.NoAction,
                                scheduleDate: new Date(),
                                assignedUsers: [this.loggedInUserId],
                                assignedGroups: [],
                                jiraLinks: [],
                                priority: 0,
                                latestNote: {
                                    text: ""
                                }
                            },
                        });
                    }
                })
            )
            .subscribe({
                next: (r) => {
                    if (r.success) {
                        this.action = r.jobAction;

                        // Determine whether the job is a Rock so we can lock the
                        // action-type dropdown. Existing actions already carry
                        // the job's flag; for a brand-new action we only have the
                        // jobId, so look the job up.
                        if (this.action.jobActionId) {
                            this.isRockJob = !!this.action.rocks;
                        } else if (this.action.jobId) {
                            this.jobService.getJob(this.action.jobId).subscribe({
                                next: (job) => {
                                    this.isRockJob = !!job?.rocks;
                                    this._changeDetector.detectChanges();
                                },
                            });
                        }

                        this.assignmentModel = {
                            userIds: [...(this.action.assignedUsers || [])],
                            groupIds: [...(this.action.assignedGroups || [])]
                        };

                        // Pre-populate the "move to another job" Job dropdown
                        // with the jobs for the action's current client, so it
                        // shows up already selected on the current job. Only
                        // relevant when editing an existing action - a brand
                        // new action's job is already fixed by context.
                        if (this.action.jobActionId && this.action.clientId) {
                            this.loadMoveJobs(this.action.clientId);
                        }

                        if (r.jobAction) {
                            this.recurrence = this.action.actionRecurrence;

                            if (this.recurrence) {
                                switch (this.recurrence.recurrenceType) {
                                    case RecurrenceType.Daily:
                                        this._recurrenceType = 'daily';
                                        break;
                                    case RecurrenceType.Weekly:
                                        this._recurrenceType = 'weekly';
                                        break;
                                    case RecurrenceType.Monthly:
                                        this._recurrenceType = 'monthly';
                                        break;
                                    case RecurrenceType.Yearly:
                                        this._recurrenceType = 'yearly';
                                        break;
                                    default:
                                }
                            }

                            if (this.action.waitingGroupId != null) {
                                // tell the picker “group-5” so it can match it
                                this.waitingOnUserOrGroupId = `group-${this.action.waitingGroupId}`;
                                this.waitingGroupIds = [this.action.waitingGroupId];
                                this.waitingUserIds  = [];
                            } else if (this.action.waitingUserId) {
                                this.waitingOnUserOrGroupId = this.action.waitingUserId;
                                this.waitingUserIds  = [this.action.waitingUserId];
                                this.waitingGroupIds = [];
                            } else {
                                this.waitingOnUserOrGroupId = null;
                                this.waitingUserIds  = [];
                                this.waitingGroupIds = [];
                            }
                            
                            this.userIds = this.action?.assignedUsers ?? [
                                this.loggedInUserId,
                            ];
                            this.groupIds = this.action?.assignedGroups ?? [];
                            this.userSelectValid =
                                this.userIds.length > 0 ||
                                this.groupIds.length > 0;

                            this.action.actionStatus =
                                this.nextActionStatus ||
                                this.action.actionStatus;
                            if (this.action.jobActionId && this.action.jiraLinks?.length == 0 && this.propertyKey === 'jira') {
                                this.action.jiraLinks = [{ 
                                    url: '', 
                                    title: '', 
                                    jobActionId: this.action.jobActionId,
                                    jiraLinkId: 0,
                                    board: '', 
                                    issue: '', 
                                    isIssue: false }];
                                this.showEditJiraLinks = true;
                            }
                        }
                    } else {
                        this.error = r.error;
                    }
                },
                error: (error) => {
                    this.error = error;
                },
            });
    }

    get recurrenceType(): string {
        return this._recurrenceType;
    }

    get userOrGroup(): UserOrGroup {
        return this._userOrGroup;
    }

    get statuses() {
        if (!this.jobActionId) {
            return statuses.filter(
                (s) =>
                    s.value !== ActionStatus.Completed &&
                    s.value !== ActionStatus.Archived
            );
        }

        return statuses;
    }

    get priorities() {
        return priorities;
    }

    get recurranceIntervals() {
        return recurranceIntervals;
    }

    set recurrenceType(value: string) {
        this._recurrenceType = value;
        if (!this.recurrence && this._recurrenceType !== 'none') {
            var recurrence = new ActionRecurrence();
            if (this.action.scheduleDate) {
                var deadline = moment(this.action.scheduleDate);
                recurrence.dayOfMonth = deadline.get('date');
                recurrence.monthOfYear = deadline.get('month') + 1;
                switch (deadline.weekday()) {
                    case 0:
                        recurrence.sunday = true;
                        break;
                    case 1:
                        recurrence.monday = true;
                        break;
                    case 2:
                        recurrence.tuesday = true;
                        break;
                    case 3:
                        recurrence.wednesday = true;
                        break;
                    case 4:
                        recurrence.thursday = true;
                        break;
                    case 5:
                        recurrence.friday = true;
                        break;
                    case 6:
                        recurrence.saturday = true;
                        break;
                    default:
                }
            } else {
                recurrence.dayOfMonth = 1;
                recurrence.monthOfYear = 1;
            }
            recurrence.everyNWeeks = 1;
            this.recurrence = recurrence;
        } else if (this._recurrenceType === 'none') {
            this.recurrence = null;
        }
        if (this.recurrence) {
            switch (this._recurrenceType) {
                case 'daily':
                    this.recurrence.recurrenceType = RecurrenceType.Daily;
                    break;
                case 'weekly':
                    this.recurrence.recurrenceType = RecurrenceType.Weekly;
                    break;
                case 'monthly':
                    this.recurrence.recurrenceType = RecurrenceType.Monthly;
                    break;
                case 'yearly':
                    this.recurrence.recurrenceType = RecurrenceType.Yearly;
                    break;
                default:
            }
        }
    }

    onUserOrGroupSelected({
        userIds,
        groupIds,
    }: {
        userIds: string[];
        groupIds: number[];
    }) {
        this.action.assignedUsers = userIds;
        this.action.assignedGroups = groupIds;
        this.userSelectValid = userIds.length > 0 || groupIds.length > 0;
        this.form.form.markAsDirty();
    }

    onScheduleDateChange() {
        if (!this.action.scheduleDate) {
            this.action.deadlineDate = null;
        }
    }

    onClearDate(prop: string) {
        this.action[prop] = null;
    }

    // Jobs for one client, taken from the app-wide cached list of live jobs
    // (the same lightweight list the timer job picker uses). After the first
    // fetch this is instant, instead of a full job search per client. That
    // list only carries the client's display name, so we match on it.
    private _getMoveJobs(clientId: number): Observable<ShortJob[]> {
        const client = this.allClients.find(c => c.clientId === clientId);
        const clientName = client
            ? (client.displayName || client.clientName)
            : this.action?.clientName;

        return this.jobService
            .getAllLiveJobsCached()
            .pipe(map(jobs => (jobs ?? []).filter(j => j.clientName === clientName)));
    }

    loadMoveJobs(clientId: number) {
        this.moveJobsLoading = true;
        this._getMoveJobs(clientId).subscribe({
            next: jobs => {
                this.moveJobs = jobs;
                this.moveJobsLoading = false;
            },
            error: () => (this.moveJobsLoading = false),
        });
    }

    onMoveClientChange(clientId: number | '' | null) {
        const previousJobId = this.action.jobId;
        this.moveJobs = [];

        if (clientId === '' || clientId == null || isNaN(+clientId)) {
            return;
        }

        const id = Number(clientId);
        this.moveJobsLoading = true;
        this._getMoveJobs(id).subscribe({
            next: jobs => {
                this.moveJobs = jobs;
                this.moveJobsLoading = false;

                // Keep the current job selected if it still belongs to the
                // (re-)chosen client. Otherwise clear it so the dropdown shows
                // "Select a job for this client" and the user picks one
                // deliberately. Not marked dirty: changing client isn't an
                // error, and Save stays disabled until a job is chosen anyway.
                if (!this.moveJobs.some(j => j.jobId === Number(previousJobId))) {
                    this.action.jobId = null;
                }
            },
            error: () => (this.moveJobsLoading = false),
        });
    }

    isControlValid(modelValue) {
        return !!modelValue;
    }

    get canSaveWaiting(): boolean {
        // always allowed unless you’re in “Waiting” w/o a picked user/group
        if (this.action.actionStatus === ActionStatus.Waiting) {
            return !!this.waitingOnUserOrGroupId;
        }
        return true;
    }

    handleSubmit(isRecurring: boolean) {
        // Return if already submitting
        if (this.submitting) return;
        this.submitting = true;
        this._assignWaitingUserOrGroup();

        if (!this.action.jobActionId) {
            if (this._recurrenceType !== 'none') {
                this.action.actionRecurrence = this.recurrence;
            }
            this.actionService.create(this.action).subscribe({
                next: (response) =>
                    this.changed.emit({
                        action: response,
                        type: this.addAnother ? 'create-multi' : 'created',
                    }),
                error: (error) => (this.error = error),
                complete: () => (this.submitting = false),
            });
        } else {
            if (this._recurrenceType === 'none') {
                this.action.actionRecurrence = null;
            } else {
                this.action.actionRecurrence = this.recurrence;
            }
            this.actionService.update(this.action).subscribe({
                next: (response) =>
                    this.changed.emit({ action: response, type: 'updated' }),
                error: (error) => (this.error = error),
                complete: () => (this.submitting = false),
            });
        }
    }

    handleWeekday(day: string) {
        if (day && this.recurrence) {
            daysOfWeek
                .filter((d) => d !== day)
                .forEach((day) => {
                    this.recurrence[day] = false;
                });
        }
    }

    adjustDayNumbers() {
        if (this.recurrence) {
            switch (this.recurrence.monthOfYear) {
                case 1:
                case 3:
                case 5:
                case 7:
                case 8:
                case 10:
                case 12: {
                    this.dayNumbers = this.maxDayNumbers;
                    break;
                }
                case 2: {
                    this.dayNumbers = this.maxDayNumbers.slice(0, 28);
                    if (this.recurrence.dayOfMonth > 28) {
                        this.recurrence.dayOfMonth = 28;
                    }
                    break;
                }
                case 4: {
                }
                case 6: {
                }
                case 9: {
                }
                case 11: {
                    this.dayNumbers = this.maxDayNumbers.slice(0, 30);
                    if (this.recurrence.dayOfMonth > 30) {
                        this.recurrence.dayOfMonth = 30;
                    }
                    break;
                }
                default:
            }
        }
    }

    onSubmit() {
        this.handleSubmit(true);
    }

    onCancel() {
        this.cancel.emit();
    }

    onNotesChanged({
        notes,
        type,
    }: {
        notes: ActionNote[];
        type: 'created' | 'updated' | 'deleted';
    }) {
        this.action.notes = notes;
        this.changed.emit({ action: this.action, type });
    }

    onStatusChange(e: RadioPickerItem) {
        // normalize to numeric ActionStatus
        const newStatus = typeof e.value === 'string'
            ? parseInt(e.value, 10)
            : e.value;

        // only care when we're doing the "status" widget
        if (this.propertyKey !== 'actionStatus') {
            return;
        }

        // if the status is already set to the new value, do nothing
        if (newStatus !== ActionStatus.Waiting) {
            this.action.actionStatus = newStatus;
            return this.handleSubmit(true);
        }

        // if we’re setting the status to Waiting, we need to show the user picker
        this.action.actionStatus = ActionStatus.Waiting;
        // force the DOM to update so the dropdown shows
        this._changeDetector.detectChanges();
        // focus the "Waiting for" picker
        this.waitingUserControlRef?.nativeElement.focus();
    }

    handleWaitingUserSelected(selection: any) {
        let newVal: string | number | null = null;
        let fromUserSelect = false;

        if (selection == null) {
            newVal = null;
        } else if (typeof selection === 'string') {
            newVal = selection;
        } else if (selection.isGroup) {
            newVal = `group-${selection.group.userGroupId}`;
            fromUserSelect = true;
        } else if (selection.isUser) {
            newVal = selection.user.id;
            fromUserSelect = true;
        } else if (selection.id) {
            newVal = selection.id;
        }

        // toggle off if same click
        if (fromUserSelect &&
            newVal?.toString() === (this.waitingOnUserOrGroupId ?? '').toString()
        ) {
            this.waitingOnUserOrGroupId = null;
        } else {
            this.waitingOnUserOrGroupId = newVal;
        }

        // sync into the payload
        this._assignWaitingUserOrGroup();
        this.action.actionStatus = ActionStatus.Waiting;

        // mark dirty
        this.form.form.markAsDirty();
        this.form.form.markAsTouched();

        // re-render so the Save/Cancel section hides
        this._changeDetector.detectChanges();

        // if we’re in a Waiting status and the user has selected a user/group,
        if (this.canSaveWaiting && !!this.action.jobActionId) {
            this.handleSubmit(true);
        }
    }

    onPriorityChange(e: RadioPickerItem) {
        if (this.propertyKey === 'priority') {
            this.handleSubmit(true);
        }
    }

    private _assignWaitingUserOrGroup() {
        const val = this.waitingOnUserOrGroupId;
        if (!val) {
            this.action.waitingUserId = null;
            this.action.waitingGroupId = null;
        } else if (typeof val === 'string' && val.startsWith('group-')) {
            // “group-5” → waitingGroupId = 5
            this.action.waitingGroupId = parseInt(val.replace('group-', ''), 10);
            this.action.waitingUserId  = null;
        } else if (typeof val === 'string') {
            // plain user-ID string
            this.action.waitingUserId  = val;
            this.action.waitingGroupId = null;
        } else {
            // numeric fallback (won’t normally happen)
            this.action.waitingGroupId = val as number;
            this.action.waitingUserId  = null;
        }
    }

    onEditorCreated(editor: Quill) {
        // Remove tabbing in the editor
        //editor.setSelection(editor.getLength(), editor.getLength());
        delete editor.keyboard['bindings']['9'];
    }

    addJiraLink() {
        this.action.jiraLinks.push(
            {
                url: '',
                title: '',
                jobActionId: this.action.jobActionId,
                jiraLinkId: 0,
                board: '',
                issue: '',
                isIssue: false
            });
    }

    removeJiraLink(index: number) {
        this.action.jiraLinks.splice(index, 1);
        // mark the form as dirty
        this.form.form.markAsDirty();
    }

    onLeaveJiraLink(index: number) {
        const link = this.action.jiraLinks[index];
        const url = link.url;
        const title = link.title;
        if (!url || title) {
            return;
        }
        link.title = '';
        this.actionService.getJiraName(url).subscribe((result) => {
            if (result.success) {
                link.title = result.jiraName;
                this.form.form.markAsDirty();
            } else {
                //link.title = 'Unable to find Jira name';
            }
        });
    }
}
